/**
 * Section-based navigation sidebar: renders titled groups of NavLinks (active
 * link highlighted) with an optional footer node. Fixed and slide-in on mobile,
 * with a hamburger open button, close button, and backdrop overlay; static
 * 64-wide column on md+ screens. Used by UserHomepage (/account/*) and
 * AdminHomepage (/admin/*) for their sub-navigation.
 */
import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";

export interface SidebarItem {
    label: string;
    url: string;
    onClick?: () => void;
}

export interface SidebarSection {
    title: string;
    items: SidebarItem[];
}

interface SidebarProps {
    sections: SidebarSection[];
    footer?: React.ReactNode;
    className?: string;
}

export const Sidebar: React.FC<SidebarProps> = ({ sections, footer, className = "" }) => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    return (
        <>
            {/* Mobile Menu Button - Only visible on mobile when sidebar is closed */}
            <button
                onClick={() => setIsSidebarOpen(true)}
                aria-label="Open sidebar"
                aria-expanded={isSidebarOpen}
                className={`md:hidden fixed left-0 top-[4.5rem] z-40 bg-white p-2 rounded-r-lg shadow-md hover:bg-slate-100 transition-opacity duration-300 ${
                    isSidebarOpen ? "opacity-0" : "opacity-100"
                }`}
            >
                <Menu className="w-6 h-6" />
            </button>

            {/* Sidebar */}
            <div
                className={`
                    fixed md:relative top-16 left-0 h-[calc(100%-4rem)] bg-white z-30
                    transform transition-transform duration-300 ease-in-out
                    md:transform-none md:top-0 md:h-full
                    ${isSidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
                    w-full max-w-64 md:w-64 flex flex-col ${className}
                `}
            >
                {/* Close button - only visible on mobile */}
                <button
                    onClick={() => setIsSidebarOpen(false)}
                    className="md:hidden absolute top-4 right-4 p-2 hover:bg-slate-100 rounded-full z-40"
                >
                    <X className="w-6 h-6" />
                </button>

                <div className="p-8 h-full overflow-auto shadow-[4px_0_8px_rgba(0,0,0,0.1)] flex flex-col">
                    <div className="w-full h-full flex flex-col overflow-auto">
                        {sections.map((section, sectionIndex) => (
                            <React.Fragment key={`${section.title}-${sectionIndex}`}>
                                <h1 className="text-2xl">{section.title}</h1>
                                <nav className="flex flex-col space-y-2 py-4">
                                    {section.items.map((item, itemIndex) => (
                                        <NavLink
                                            key={`${item.label}-${itemIndex}`}
                                            to={item.url}
                                            className={({ isActive }) =>
                                                `w-full text-left p-2 rounded font-porsui ${
                                                    isActive
                                                        ? "bg-gray-100 font-medium"
                                                        : "hover:bg-gray-50"
                                                }`
                                            }
                                            onClick={() => {
                                                setIsSidebarOpen(false);
                                                item.onClick?.();
                                            }}
                                        >
                                            {item.label}
                                        </NavLink>
                                    ))}
                                </nav>
                            </React.Fragment>
                        ))}
                    </div>

                    {/* Footer content */}
                    {footer && <div className="mt-auto">{footer}</div>}
                </div>
            </div>

            {/* Overlay - only visible on mobile when sidebar is open */}
            {isSidebarOpen && (
                <div
                    className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-20"
                    onClick={() => setIsSidebarOpen(false)}
                />
            )}
        </>
    );
};
